import React from 'react';

import classNames from 'classnames';
import cardValidator from 'card-validator';
import { Formik, FormikProps } from 'formik';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import CloseIcon from '@material-ui/icons/Close';
import IconButton from '@material-ui/core/IconButton';
import InfoOutlined from '@material-ui/icons/InfoOutlined';

import Dialog from '@material-ui/core/Dialog';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import MuiDialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import Popover from '@material-ui/core/Popover';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';

import Button from 'components/CustomButtons/Button';
import { BodyMBold } from 'components/Typography/Typography';

import validationProps from 'components/PaymentMethod/helper/validation';

import { getPaymentMethodLabels } from 'redux/modules/dynamicLabels/selectors';
import { CardDetails, CardPaymentToken, CardDetails as ICardDetails, PayWith } from '../../type';

import cardCVV from 'assets/img/cards/cvv-number-v2.png';

import { makeStyles } from '@material-ui/core/styles';
import styles, { AntSwitch as Switch } from 'assets/jss/material-dashboard-pro-react/components/paymentMethodsStyle';

interface AddCardProps {
	onClose: () => void;
	isOpen: boolean;
	onAddCard: (_card: CardPaymentToken) => void;
}

// @ts-ignore
const useStyles = makeStyles(styles);

const AddCard = ({ isOpen, onAddCard, onClose }: AddCardProps) => {
	const classes = useStyles();

	const paymentMethodLabels = useSelector(getPaymentMethodLabels);

	const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
	const open = Boolean(anchorEl);

	const handlePopoverOpen = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePopoverClose = () => {
		setAnchorEl(null);
	};

	const onSubmit = (values: CardDetails) => {
		onAddCard({
			bankName: '',
			cardExpiry: values.cardExpire,
			cardNumber: values.cardNumber,
			cardType: '',
			cardCVV: values.cardCVV,
			daysTillExpiry: 0,
			default: true,
			firstName: '',
			lastName: '',
			id: (+new Date()).toString(),
			paymentGateway: 'C',
			tokenType: PayWith.CARD,
			isUnSaved: true,
			savedOption: values.savedOption,
		});
	};

	const FormSchema = Yup.object().shape({
		name: Yup.string()
			.required(paymentMethodLabels?.nameRequired)
			.matches(/^[a-zA-Z ]*$/, paymentMethodLabels?.nameInvalid)
			.min(1, paymentMethodLabels?.nameInvalid)
			.max(80, paymentMethodLabels?.nameInvalid)
			.test('not-blank', paymentMethodLabels?.nameInvalid, (value) => value?.trim() !== ''),
		cardNumber: Yup.string()
			.required(paymentMethodLabels?.cardNumberRequired)
			.test(
				'card number',
				paymentMethodLabels?.cardNumberInvalid,
				(value: string): boolean => cardValidator.number(value).isValid
			),
		cardExpire: Yup.string()
			.required(paymentMethodLabels?.cardExpiryRequired)
			.test(
				'card-expiry',
				paymentMethodLabels?.cardExpiryInvalid,
				(value: string): boolean => cardValidator.expirationDate(value).isValid
			),
		cardCVV: Yup.string()
			.required(paymentMethodLabels?.cardCVVRequired)
			.matches(/^\d{3,4}$/, paymentMethodLabels?.cvvInvalid)
			.test('card-cvv', paymentMethodLabels?.cvvInvalid, function(value: string): boolean {
				const cardValid = cardValidator.number(this.parent?.cardNumber);
				return cardValidator.cvv(value, cardValid.card?.code?.size ?? 3).isValid;
			}),
	});

	return (
		<Dialog aria-labelledby="customized-dialog-title" open={isOpen}>
			<MuiDialogTitle disableTypography>
				<BodyMBold customClassName={classes.marginBottomNone}>{paymentMethodLabels?.add_card}</BodyMBold>
				{onClose && (
					<IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
						<CloseIcon />
					</IconButton>
				)}
			</MuiDialogTitle>
			<MuiDialogContent dividers className={classes.addCardDialog}>
				<Formik
					onSubmit={onSubmit}
					validationSchema={FormSchema}
					initialValues={{
						name: '',
						cardNumber: '',
						cardCVV: '',
						cardExpire: '',
						savedOption: false,
					}}
				>
					{(props: FormikProps<Omit<ICardDetails, 'email' | 'defaultOption'>>) => {
						return (
							<>
								<div className={classes.billDetailsContainer}>
									<TextField
										placeholder={paymentMethodLabels?.nameOnCard}
										className={classes.billingEmailInput}
										style={{ margin: 8 }}
										fullWidth
										onChange={props.handleChange}
										onBlur={props.handleBlur}
										margin="normal"
										InputLabelProps={{
											shrink: true,
										}}
										inputProps={{ maxLength: 40 }}
										{...validationProps('name', props.errors, props.touched)}
									/>
								</div>
								<div className={classes.billDetailsContainer}>
									<TextField
										placeholder={paymentMethodLabels?.cardNumber}
										className={classes.billingEmailInput}
										style={{ margin: 8 }}
										fullWidth
										onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
											e.target.value = e.target.value.replace(/\s/g, '');
											props.handleChange(e);
										}}
										onBlur={props.handleBlur}
										margin="normal"
										InputLabelProps={{
											shrink: true,
										}}
										value={props.values.cardNumber.replace(
											/(\d{4})(\d{4})(\d{4})(\d{4})/,
											'$1 $2 $3 $4'
										)}
										inputProps={{ maxLength: 19 }}
										{...validationProps('cardNumber', props.errors, props.touched)}
									/>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.paddingBottomNone)}>
									<Grid direction="row" container spacing={3} className={classes.bottomSpace}>
										<Grid item xs={6}>
											<TextField
												placeholder={paymentMethodLabels?.MM_YY}
												className={classes.billingEmailInput}
												style={{ margin: 8 }}
												fullWidth
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													e.target.value = e.target.value.replace('/', '');
													props.handleChange(e);
												}}
												onBlur={props.handleBlur}
												margin="normal"
												InputLabelProps={{
													shrink: true,
												}}
												inputProps={{
													maxLength: 5,
												}}
												value={props.values?.cardExpire?.match(/.{1,2}/g)?.join('/')}
												{...validationProps('cardExpire', props.errors, props.touched)}
											/>
										</Grid>
										<Grid item xs={6}>
											<div>
												<TextField
													placeholder={paymentMethodLabels?.CVV}
													className={classes.billingEmailInput}
													fullWidth
													onChange={props.handleChange}
													onBlur={props.handleBlur}
													InputProps={{
														endAdornment: (
															<InputAdornment position="end">
																<div
																	onMouseEnter={handlePopoverOpen}
																	onMouseLeave={handlePopoverClose}
																	data-testId="payment-help"
																>
																	<InfoOutlined />
																</div>
															</InputAdornment>
														),
													}}
													inputProps={{
														maxLength: 4,
														minLength: 3,
													}}
													value={props.values.cardCVV}
													{...validationProps('cardCVV', props.errors, props.touched)}
												/>
												<Popover
													id="mouse-over-popover"
													open={open}
													className={classes.popover}
													classes={{
														paper: classes.paper,
													}}
													anchorEl={anchorEl}
													anchorOrigin={{
														vertical: 'bottom',
														horizontal: 'left',
													}}
													transformOrigin={{
														vertical: 'top',
														horizontal: 'right',
													}}
													onClose={handlePopoverClose}
													disableRestoreFocus
												>
													<img src={cardCVV} alt="cvv-number" />
												</Popover>
											</div>
										</Grid>
									</Grid>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.paddingBottomNone)}>
									<p className={classNames(classes.infoNote, classes.paymentNote)}>
										{paymentMethodLabels?.add_card_note}
									</p>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.paddingBottomNone)}>
									<div className={classNames(classes.accordionElement, classes.bottomSpace)}>
										<FormControlLabel
											className={classes.switchLabel}
											control={
												<Switch
													id="save_card_details"
													name="savedOption"
													onChange={props.handleChange}
												/>
											}
											label={paymentMethodLabels?.saveCardToWallet}
										/>
									</div>
								</div>
								<div className={classNames(classes.billDetailsContainer, classes.addCardDialogFooter)}>
									<Button color="secondary-light" onClick={onClose}>
										{paymentMethodLabels.cancel}
									</Button>
									<Button type="submit" color="primary-light" onClick={props.submitForm}>
										{paymentMethodLabels.add}
									</Button>
								</div>
							</>
						);
					}}
				</Formik>
			</MuiDialogContent>
		</Dialog>
	);
};

export default AddCard;
